import { Box, Center, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

const LOADING_DIAMOND_SRC = '/animations/loading_diamond.json';
const CANVAS_SIZE = 160;

export const CHECKOUT_STOCK_CHECK_OVERLAY_TEST_ID =
  'checkout-stock-check-overlay';
export const CHECKOUT_STOCK_CHECK_LOTTIE_TEST_ID =
  'checkout-stock-check-lottie';
export const CHECKOUT_STOCK_CHECK_FALLBACK_TEST_ID =
  'checkout-stock-check-fallback';

export const CHECKOUT_STOCK_CHECK_COPY =
  'Pengecekan Stok Sedang Berlangsung, jangan Menutup Halaman ini';

const DiamondFallback = () => (
  <Box
    aria-hidden="true"
    data-testid={CHECKOUT_STOCK_CHECK_FALLBACK_TEST_ID}
    h={`${CANVAS_SIZE}px`}
    position="relative"
    w={`${CANVAS_SIZE}px`}
  >
    <Box
      border="3px solid"
      borderColor="#005BBB"
      h="72px"
      left="50%"
      position="absolute"
      top="50%"
      transform="translate(-50%, -50%) rotate(45deg)"
      w="72px"
    />
    <Box
      bg="#E11D25"
      borderRadius="full"
      bottom="44px"
      h="18px"
      position="absolute"
      right="44px"
      w="18px"
    />
  </Box>
);

const LoadingDiamondAnimation = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || showFallback) return undefined;

    let isDisposed = false;
    let dotLottie: { destroy: () => void } | null = null;
    let rafId = 0;

    const waitForLayout = () =>
      new Promise<void>((resolve) => {
        rafId = window.requestAnimationFrame(() => {
          rafId = window.requestAnimationFrame(() => resolve());
        });
      });

    const loadAnimation = async () => {
      try {
        await waitForLayout();
        if (isDisposed) return;

        if (canvas.clientWidth <= 0 || canvas.clientHeight <= 0) {
          setShowFallback(true);
          return;
        }

        const { DotLottie } = await import('@lottiefiles/dotlottie-web');
        if (isDisposed) return;

        dotLottie = new DotLottie({
          autoplay: true,
          canvas,
          layout: {
            align: [0.5, 0.5],
            fit: 'contain',
          },
          loop: true,
          renderConfig: {
            autoResize: false,
            devicePixelRatio: 1,
          },
          src: LOADING_DIAMOND_SRC,
        });
      } catch {
        if (!isDisposed) {
          setShowFallback(true);
        }
      }
    };

    void loadAnimation();

    return () => {
      isDisposed = true;
      window.cancelAnimationFrame(rafId);
      try {
        dotLottie?.destroy();
      } catch {
        // DotLottie may throw during teardown after a WASM render error.
      }
    };
  }, [showFallback]);

  if (showFallback) {
    return <DiamondFallback />;
  }

  return (
    <Box aria-hidden="true" h={`${CANVAS_SIZE}px`} w={`${CANVAS_SIZE}px`}>
      <canvas
        data-testid={CHECKOUT_STOCK_CHECK_LOTTIE_TEST_ID}
        height={CANVAS_SIZE}
        ref={canvasRef}
        style={{ display: 'block', height: '100%', width: '100%' }}
        width={CANVAS_SIZE}
      />
    </Box>
  );
};

export const CheckoutStockCheckOverlay = () => {
  return (
    <Box
      alignItems="center"
      bg="rgba(0, 0, 0, 0.5)"
      bottom={0}
      data-testid={CHECKOUT_STOCK_CHECK_OVERLAY_TEST_ID}
      display="flex"
      justifyContent="center"
      left={0}
      pointerEvents="all"
      position="fixed"
      right={0}
      top={0}
      zIndex={1400}
    >
      <Center
        bg="white"
        borderRadius="12px"
        boxShadow="lg"
        maxW="320px"
        mx={4}
        px={8}
        py={10}
        w="full"
      >
        <VStack spacing={6}>
          <LoadingDiamondAnimation />
          <Text
            color="gray.800"
            fontSize="md"
            fontWeight="bold"
            lineHeight="1.4"
            textAlign="center"
          >
            {CHECKOUT_STOCK_CHECK_COPY}
          </Text>
        </VStack>
      </Center>
    </Box>
  );
};
